import {
  AutoAwesome,
  Check,
  Close,
  FilterList,
  MenuOpen,
  SearchOutlined,
  Undo,
} from '@mui/icons-material';
import {
  Alert,
  AppBar,
  Box,
  ButtonGroup,
  Divider,
  Grid,
  IconButton,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import React, {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { Button } from '~/components/Button';
import { SearchForm } from '~/components/SearchForm';
import useRoute from '~/hooks/useRoute';
import { useScreenSize } from '~/hooks/useScreenSize';
import useSearchFormatErrors from '~/hooks/useSearchFormatErrors';
import { FiltersMenu } from '~/scenes/reports/FiltersMenu';
import { isEmptySearch } from '~/scripts/isEmptySearch';
import { isSingleReportSearch } from '~/scripts/isSingleReportSearch';
import type { Filters, Search, Searches } from '~/types';

interface ReportEditorProps {
  abortSearch: () => void;
  revertSearchToLastUrl: () => void;
  onUpdateClick: (
    search: Searches.SearchObject | null,
    filters: Filters.Filters
  ) => void;
  temporarySearch: Searches.SearchObject | null;
  setTemporarySearch: Dispatch<SetStateAction<Searches.SearchObject>>;
  onDiscardChangesClick: () => void;
  newFilters: Filters.Filters;
  facets: Search.Facets | undefined | null;
  name: string;
  isLoading: boolean;
  isSearchDirty: boolean;
  isFiltersDirty: boolean;
  handleFiltersChange: (updates: Filters.Filters) => void;
  onClose: () => void;
}
export default function ReportEditor({
  revertSearchToLastUrl,
  onUpdateClick,
  temporarySearch,
  setTemporarySearch,
  onDiscardChangesClick,
  newFilters,
  facets,
  name,
  isLoading,
  isSearchDirty,
  isFiltersDirty,
  abortSearch,
  handleFiltersChange,
  onClose,
}: ReportEditorProps) {
  const [activeTab, setActiveTab] = useState<number>(0);
  const route = useRoute();
  const isNewReport = useMemo(() => !route.params.uuid, [route.params.uuid]);
  const warningMessages = useSearchFormatErrors(temporarySearch);

  return (
    <>
      <Grid item xs={12} sx={{ pb: 4 }}>
        <AppBar
          position="relative"
          sx={{ width: '100%' /*pr: 8*/ }}
          color="transparent"
          elevation={0}>
          {/* <IconButton sx={{ position: 'absolute', right: 12, top: 12 }}>
            <MenuOpen />
          </IconButton> */}
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            indicatorColor="secondary"
            textColor="inherit"
            variant="fullWidth"
            aria-label="Toggle between search and filters">
            <Tab
              label="Search"
              icon={<SearchOutlined />}
              iconPosition="start"
              sx={{
                minHeight: 'unset',
                py: 2.42,
              }}
            />
            <Tab
              label={
                <>
                  Filters <AutoAwesome color="secondary" sx={{ ml: 1 }} />
                </>
              }
              icon={<FilterList />}
              iconPosition="start"
              sx={{
                minHeight: 'unset',
                py: 2.42,
              }}
            />
          </Tabs>
        </AppBar>
        <Divider />
        <Grid
          id="search-tab"
          item
          xs={12}
          role="tabpanel"
          hidden={activeTab !== 0}>
          {!isNewReport && (
            <Header
              onUpdateClick={onUpdateClick}
              temporarySearch={temporarySearch}
              newFilters={newFilters}
              onDiscardChangesClick={onDiscardChangesClick}
              isSearchDirty={isSearchDirty}
              isFiltersDirty={isFiltersDirty}
              hasErrors={warningMessages.length > 0}
              isLoading={isLoading}
              abortSearch={abortSearch}
              revertSearchToLastUrl={revertSearchToLastUrl}
              onClose={onClose}>
              Edit Search
            </Header>
          )}
          {warningMessages.length > 0 && (
            <Alert severity="warning" sx={{ px: 3, py: 2 }}>
              {warningMessages.join(', ')}
            </Alert>
          )}
          <SearchForm
            sx={{ px: 1, py: 2 }}
            search={temporarySearch}
            setSearch={setTemporarySearch}
            isLoading={isLoading}
          />
        </Grid>
        <Grid
          id="filters-tab"
          item
          xs={12}
          role="tabpanel"
          hidden={activeTab !== 1}>
          {!isNewReport && (
            <Header
              onUpdateClick={onUpdateClick}
              temporarySearch={temporarySearch}
              newFilters={newFilters}
              onDiscardChangesClick={onDiscardChangesClick}
              isSearchDirty={isSearchDirty}
              isFiltersDirty={isFiltersDirty}
              hasErrors={false}
              isLoading={isLoading}
              abortSearch={abortSearch}
              revertSearchToLastUrl={revertSearchToLastUrl}
              onClose={onClose}>
              Edit Filters
            </Header>
          )}
          <FiltersMenu
            filters={newFilters}
            facets={facets}
            name={name}
            isLoading={isLoading}
            isSingleReportUuid={
              isSingleReportSearch(temporarySearch)
                ? temporarySearch?.all[0]?.value || ''
                : ''
            }
            onChange={(updates) => {
              handleFiltersChange(updates);
            }}
          />
        </Grid>
      </Grid>
    </>
  );
}

const Header = ({
  children,
  onUpdateClick,
  temporarySearch,
  newFilters,
  abortSearch,
  revertSearchToLastUrl,
  onDiscardChangesClick,
  isSearchDirty,
  isFiltersDirty,
  hasErrors,
  isLoading,
  onClose,
}: {
  children: React.ReactNode;
  onUpdateClick: (
    search: Searches.SearchObject | null,
    filters: Filters.Filters
  ) => void;
  temporarySearch: Searches.SearchObject | null;
  newFilters: Filters.Filters;
  abortSearch: () => void;
  revertSearchToLastUrl: () => void;
  onDiscardChangesClick: () => void;
  isSearchDirty: boolean;
  isLoading: boolean;
  isFiltersDirty: boolean;
  hasErrors: boolean;
  onClose: () => void;
}) => {
  const { lgAndDown } = useScreenSize();

  const handleSave = useCallback(() => {
    abortSearch();
    onUpdateClick(temporarySearch, newFilters);
    if (lgAndDown) {
      onClose();
    }
  }, [
    abortSearch,
    lgAndDown,
    temporarySearch,
    newFilters,
    onUpdateClick,
    onClose,
  ]);

  const handleUndo = useCallback(() => {
    abortSearch();
    revertSearchToLastUrl();
    onDiscardChangesClick();
    if (lgAndDown) {
      onClose();
    }
  }, [
    abortSearch,
    lgAndDown,
    onDiscardChangesClick,
    onClose,
    revertSearchToLastUrl,
  ]);

  return (
    <>
      <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center' }}>
        <Typography variant="h6">{children}</Typography>
        <Box sx={{ display: 'flex', flexGrow: 1 }}></Box>
        {
          <ButtonGroup variant="outlined" color="primary" size="small">
            {(isSearchDirty || isFiltersDirty) && (
              <Button startIcon={<Undo />} onClick={handleUndo}>
                Undo
              </Button>
            )}
            {(isSearchDirty || isFiltersDirty) &&
              !isEmptySearch(temporarySearch) && (
                <Button
                  variant="contained"
                  startIcon={<Check />}
                  disabled={hasErrors || isLoading}
                  onClick={handleSave}>
                  Apply
                </Button>
              )}
          </ButtonGroup>
        }
        {lgAndDown ? (
          <Button
            onClick={onClose}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
            startIcon={<Close />}>
            Close
          </Button>
        ) : (
          <IconButton onClick={onClose} sx={{ ml: 2 }} color="primary">
            <MenuOpen />
          </IconButton>
        )}
      </Box>
      <Divider />
    </>
  );
};
